/**
 * Мрежа — the co-ownership network.
 *
 * Two thresholds decide what is analysed and both are shown beside the
 * result: a person with one or two company involvements is almost always an
 * incidental manager, and a single company in common is too ordinary a
 * coincidence to mean anything. Changing either changes the picture, which
 * is why any published claim has to state the values it came from.
 *
 * Links are drawn by what the shared companies are, not by how many: two
 * people holding two dormant companies together is a different fact from two
 * holding two that win public contracts.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import cytoscape, { type ElementDefinition } from 'cytoscape';
import { graphql } from '../gql';
import { request } from '../lib/graphql';
import { ErrorState, Loading, PageHead, SectionHead, Stat } from '../components/ui';
import { num, truncate } from '../lib/format';
import { attachTooltip } from '../lib/tooltip';
import { downloadGexf } from '../lib/gexf';

const NETWORK = graphql(`
  query PersonNetwork($minFirms: Int!, $minShared: Int!) {
    personNetwork(minFirms: $minFirms, minShared: $minShared) {
      minFirms
      minShared
      eligible
      people { hash name firms tier publicFirms }
      links { source target shared kind companies }
    }
  }
`);

const TIER_SIZE: Record<string, number> = {
  'Организатор': 66, 'Ключов': 54, 'Активен': 44, 'Участник': 34,
};

/* Colour carries the link's meaning, which is the one thing here that is a
   judgement about substance rather than a count. */
const KIND_STYLE: Record<string, { colour: string; label: string; note: string }> = {
  financial: {
    colour: '#e66767',
    label: 'Финансова',
    note: 'всички общи дружества носят публичен дълг или публични пари',
  },
  mixed: {
    colour: '#d95926',
    label: 'Смесена',
    note: 'част от общите дружества имат финансово значение, част не',
  },
  structural: {
    colour: '#5a5a66',
    label: 'Структурна',
    note: 'общите дружества нямат нито дълг, нито поръчки, нито финансиране',
  },
};

export function NetworkPage() {
  const [minFirms, setMinFirms] = useState(3);
  const [minShared, setMinShared] = useState(2);
  const holder = useRef<HTMLDivElement>(null);

  const query = useQuery({
    queryKey: ['network', minFirms, minShared],
    queryFn: () => request(NETWORK, { minFirms, minShared }),
    placeholderData: keepPreviousData,
  });

  const net = query.data?.personNetwork;

  const elements = useMemo<ElementDefinition[]>(() => {
    if (!net) return [];
    // only people who ended up with a link: an isolated node says nothing
    // the list pages do not say better
    const linked = new Set<string>();
    for (const link of net.links) { linked.add(link.source); linked.add(link.target); }
    const nodes = net.people.filter((person) => linked.has(person.hash)).map((person) => ({
      data: {
        id: person.hash,
        type: 'person',
        label: truncate(person.name ?? 'лице', 26),
        tier: person.tier,
        firms: person.firms,
        publicFirms: person.publicFirms,
        href: `#/people/${person.hash}`,
        details: {
          'Степен': person.tier,
          'Дружества': person.firms,
          'От тях с публични пари или дълг': person.publicFirms,
        },
      },
    }));
    const edges = net.links.map((link, index) => ({
      data: {
        id: `l${index}`,
        source: link.source,
        target: link.target,
        label: `${link.shared} общи`,
        kind: link.kind,
        companies: link.companies.join(' · '),
      },
    }));
    return [...nodes, ...edges];
  }, [net]);

  useEffect(() => {
    if (!holder.current || elements.length === 0) return undefined;
    const instance = cytoscape({
      container: holder.current,
      elements,
      wheelSensitivity: 0.25,
      style: [
        {
          selector: 'node',
          style: {
            label: 'data(label)',
            'background-color': '#d95926',
            width: ((node: cytoscape.NodeSingular) =>
              TIER_SIZE[node.data('tier') as string] ?? 34) as never,
            height: ((node: cytoscape.NodeSingular) =>
              (TIER_SIZE[node.data('tier') as string] ?? 34) * 0.8) as never,
            shape: 'diamond' as never,
            color: '#ece7dd',
            'font-size': 11,
            'font-family': 'IBM Plex Sans, sans-serif',
            'text-valign': 'bottom',
            'text-margin-y': 6,
            'text-wrap': 'wrap',
            'text-max-width': '130px',
            'text-outline-width': 3,
            'text-outline-color': '#050508',
            'border-width': 2,
            'border-color': '#050508',
          },
        },
        {
          selector: 'edge',
          style: {
            width: 2,
            'curve-style': 'bezier',
            'line-color': (edge: cytoscape.EdgeSingular) =>
              KIND_STYLE[edge.data('kind') as string]?.colour ?? '#3a3a44',
            label: 'data(label)',
            'font-size': 9,
            'font-family': 'IBM Plex Mono, monospace',
            color: '#a19c92',
            'text-rotation': 'autorotate',
            'text-outline-width': 2,
            'text-outline-color': '#050508',
          },
        },
      ],
      layout: {
        name: 'cose', animate: false, padding: 40,
        nodeRepulsion: 400000, idealEdgeLength: 130, nodeOverlap: 20,
      } as never,
    });
    instance.fit(undefined, 40);
    const hideTooltip = attachTooltip(instance);
    instance.on('tap', 'node', (event) => {
      const href = event.target.data('href') as string;
      if (href) window.location.hash = href.replace(/^#/, '');
    });
    return () => { hideTooltip(); instance.destroy(); };
  }, [elements]);

  /* Exported with the thresholds in the description, because a network
     without them cannot be interpreted or reproduced. */
  const exportGexf = () => {
    if (!net) return;
    downloadGexf(
      `network-firms${net.minFirms}-shared${net.minShared}.gexf`,
      elements.filter((e) => !e.data['source']).map((e) => ({
        id: String(e.data['id']),
        label: String(e.data['label'] ?? ''),
        attributes: {
          tier: String(e.data['tier'] ?? ''),
          firms: Number(e.data['firms'] ?? 0),
          publicFirms: Number(e.data['publicFirms'] ?? 0),
        },
      })),
      elements.filter((e) => e.data['source']).map((e) => ({
        source: String(e.data['source']),
        target: String(e.data['target']),
        label: String(e.data['label'] ?? ''),
        attributes: {
          kind: String(e.data['kind'] ?? ''),
          companies: String(e.data['companies'] ?? ''),
        },
      })),
      `Мрежа на съвместно участие. MIN_FIRMS=${net.minFirms}, `
      + `MIN_SHARED=${net.minShared}. Само Търговският регистър — кметовете идват `
      + 'от ЦИК с друг идентификатор и не участват.',
    );
  };

  const byKind = (net?.links ?? []).reduce<Record<string, number>>((acc, link) => {
    acc[link.kind] = (acc[link.kind] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <PageHead
        eyebrow="Анализ"
        title="Мрежа на съвместно участие"
        lede={'Лица, които държат няколко дружества, и връзките между тях. Праговете '
          + 'определят кой попада в анализа и кои съвпадения се броят — записани са до '
          + 'резултата и трябва да се посочват във всеки публикуван материал.'}
      />

      <div className="facet-row">
        <label className="facet-label" htmlFor="min-firms">Мин. брой фирми на лице</label>
        <select className="facet-select" id="min-firms" value={String(minFirms)}
          onChange={(event) => setMinFirms(Number(event.target.value))}>
          {[2, 3, 5, 10].map((n) => <option key={n} value={String(n)}>{n}</option>)}
        </select>
        <label className="facet-label" htmlFor="min-shared">Мин. общи дружества</label>
        <select className="facet-select" id="min-shared" value={String(minShared)}
          onChange={(event) => setMinShared(Number(event.target.value))}>
          {[1, 2, 3, 4].map((n) => <option key={n} value={String(n)}>{n}</option>)}
        </select>
        <span className="facet-note">
          {`MIN_FIRMS=${net?.minFirms ?? minFirms}, MIN_SHARED=${net?.minShared ?? minShared}`}
        </span>
        {elements.length > 0 && (
          <button type="button" className="btn" onClick={exportGexf}
            title="Изтегля мрежата като GEXF за Gephi">
            Изтегли за Gephi
          </button>
        )}
      </div>

      {query.isError && <ErrorState message={(query.error).message} />}
      {query.isPending ? <Loading /> : net && (
        <>
          <div className="grid grid-4">
            <Stat value={num(net.eligible)} label="Лица над прага" />
            <Stat value={num(net.links.length)} label="Връзки" />
            <Stat value={num(byKind['financial'] ?? 0)} label="Финансови връзки" />
            <Stat value={num(byKind['mixed'] ?? 0)} label="Смесени връзки" />
          </div>

          <div className="graph-shell">
            <div className="graph-legend">
              {Object.entries(KIND_STYLE).map(([kind, meta]) => (
                <span className="legend-item" key={kind}>
                  <span className="glyph" style={{ background: meta.colour }} />
                  {meta.label}
                </span>
              ))}
              <span className="legend-item">
                <span className="glyph person" />
                размерът на възела е броят дружества
              </span>
            </div>
            {elements.length > 0
              ? <div ref={holder} id="cy" />
              : (
                <p className="state">
                  Няма връзки при тези прагове. Понижете „мин. общи дружества“, за да
                  видите по-слаби съвпадения — с уговорката, че едно общо дружество е
                  твърде често съвпадение, за да значи нещо.
                </p>
              )}
          </div>

          <section className="block">
            <SectionHead title="Как се чете" aside="методология" />
            <dl className="detail-meta detail-meta-wide">
              <div>
                <dt>Ядро</dt>
                <dd>
                  {`В мрежата влизат само лица с поне ${net.minFirms} фирмени участия. `}
                  Лице с едно или две участия почти винаги е случаен управител или
                  съдружник и присъствието му само зашумява картината.
                </dd>
              </div>
              <div>
                <dt>Връзка</dt>
                <dd>
                  {`Между двама души се отчита връзка само при поне ${net.minShared} общи `}
                  дружества. Едно общо дружество е твърде често съвпадение, за да значи нещо.
                </dd>
              </div>
              {Object.entries(KIND_STYLE).map(([kind, meta]) => (
                <div key={kind}>
                  <dt style={{ color: meta.colour }}>{`${meta.label} връзка`}</dt>
                  <dd>{meta.note}</dd>
                </div>
              ))}
              <div>
                <dt>Степени</dt>
                <dd>
                  Организатор при 20 и повече участия, Ключов при 10, Активен при 5,
                  Участник при 3. Размерът на възела следва степента.
                </dd>
              </div>
              <div>
                <dt>Обхват</dt>
                <dd>
                  Само Търговският регистър. Кметовете идват от ЦИК с друг вид
                  идентификатор и не могат да бъдат съотнесени към собственици на
                  фирми, затова не участват в тази мрежа.
                </dd>
              </div>
            </dl>
          </section>
        </>
      )}
    </>
  );
}
